/**
 * Presence Store - Tracks online players and manages presence connection
 */

import { create } from 'zustand';
import * as Colyseus from 'colyseus.js';

interface OnlinePlayer {
  wallet: string;
  status: 'online' | 'in_match' | 'in_lobby';
  joinedAt: number;
}

interface PresenceState {
  client: Colyseus.Client | null;
  room: Colyseus.Room | null;
  connected: boolean;
  totalOnline: number;
  onlinePlayers: Map<string, OnlinePlayer>;
  friends: Set<string>; // Wallet addresses of friends

  // Actions
  connect: (serverUrl: string, wallet: string) => Promise<void>;
  disconnect: () => void;
  updateStatus: (status: 'online' | 'in_match' | 'in_lobby', matchId?: string) => void;
  isOnline: (wallet: string) => boolean;
  getOnlineFriends: () => OnlinePlayer[];
  addFriend: (wallet: string) => void;
  removeFriend: (wallet: string) => void;
}

export const usePresenceStore = create<PresenceState>((set, get) => ({
  client: null,
  room: null,
  connected: false,
  totalOnline: 0,
  onlinePlayers: new Map(),
  friends: new Set(),

  connect: async (serverUrl: string, wallet: string) => {
    const state = get();
    if (state.room) {
      // Already connected
      return;
    }

    try {
      const client = new Colyseus.Client(serverUrl);
      const room = await client.joinOrCreate('presence', { wallet });

      // Listen for presence updates via messages (simpler than state schema)
      room.onMessage(
        'presence_update',
        (data: { wallet: string; status: string; matchId?: string }) => {
          set((s) => {
            const players = new Map(s.onlinePlayers);
            const existing = players.get(data.wallet);
            if (existing) {
              players.set(data.wallet, {
                ...existing,
                status: data.status as 'online' | 'in_match' | 'in_lobby',
              });
            }
            return { onlinePlayers: players };
          });
        },
      );

      room.onMessage('player_online', (data: { wallet: string; timestamp: number }) => {
        set((s) => {
          const players = new Map(s.onlinePlayers);
          players.set(data.wallet, {
            wallet: data.wallet,
            status: 'online',
            joinedAt: data.timestamp,
          });
          return { onlinePlayers: players, totalOnline: players.size };
        });
      });

      room.onMessage('player_offline', (data: { wallet: string }) => {
        set((s) => {
          const players = new Map(s.onlinePlayers);
          players.delete(data.wallet);
          return { onlinePlayers: players, totalOnline: players.size };
        });
      });

      // Heartbeat every 30 seconds
      const heartbeatInterval = setInterval(() => {
        room.send('heartbeat');
      }, 30000);

      room.onLeave(() => {
        clearInterval(heartbeatInterval);
        set({ room: null, connected: false, totalOnline: 0, onlinePlayers: new Map() });
      });

      set({ client, room, connected: true });
    } catch (error) {
      console.error('Failed to connect to presence room:', error);
    }
  },

  disconnect: () => {
    const { room } = get();
    if (room) {
      room.leave();
      set({ room: null, connected: false, totalOnline: 0, onlinePlayers: new Map() });
    }
  },

  updateStatus: (status: 'online' | 'in_match' | 'in_lobby', matchId?: string) => {
    const { room } = get();
    if (room) {
      room.send('update_status', { status, matchId });
    }
  },

  isOnline: (wallet: string) => {
    return get().onlinePlayers.has(wallet);
  },

  getOnlineFriends: () => {
    const { onlinePlayers, friends } = get();
    const onlineFriends: OnlinePlayer[] = [];
    friends.forEach((friendWallet) => {
      const player = onlinePlayers.get(friendWallet);
      if (player) {
        onlineFriends.push(player);
      }
    });
    return onlineFriends;
  },

  addFriend: (wallet: string) => {
    set((s) => {
      const friends = new Set(s.friends);
      friends.add(wallet);
      return { friends };
    });
  },

  removeFriend: (wallet: string) => {
    set((s) => {
      const friends = new Set(s.friends);
      friends.delete(wallet);
      return { friends };
    });
  },
}));
